import { createContext, useContext, useEffect, useState, useRef, ReactNode } from 'react';
import { fetchAuthSession, getCurrentUser } from '@aws-amplify/auth';
import type { CognitoUser } from '../lib/auth/cognitoAuth';

interface AmplifyAuthContextType {
  user: CognitoUser | null;
  sessionId: string | null;
  isLoading: boolean;
  error: string | null;
  convexReady: boolean;
}

const AmplifyAuthContext = createContext<AmplifyAuthContextType | undefined>(undefined);

interface AmplifyAuthProviderProps {
  children: ReactNode;
}

export function AmplifyAuthProvider({ children }: AmplifyAuthProviderProps) {
  const [authState, setAuthState] = useState<AmplifyAuthContextType>({
    user: null,
    sessionId: null,
    isLoading: true,
    error: null,
    convexReady: false,
  });

  // Flag to prevent concurrent auth initializations
  const initializingRef = useRef(false);

  // Validate JWT token with Convex and create session
  const validateWithConvex = async (idToken: string) => {
    try {
      // Use Convex client to validate JWT token
      const { ConvexHttpClient } = await import("convex/browser");
      const client = new ConvexHttpClient(import.meta.env.VITE_CONVEX_URL || "https://api.alterra.smartcronos.io");
      
      // Validate token with Convex JWT validation
      const validationResult = await client.action("components/auth/index:validateCognitoToken", {
        token: idToken
      });
      
      if (!validationResult.valid) {
        throw new Error(`JWT validation failed: ${validationResult.error}`);
      }
      
      // Create or update user in Convex
      const user = await client.mutation("components/auth/index:createOrUpdateUser", {
        cognitoSub: validationResult.user.cognitoSub,
        email: validationResult.user.email,
        name: validationResult.user.name,
        role: validationResult.user.role as any
      });

      // Create session in Convex
      const sessionId = await client.mutation("components/auth/index:createSession", {
        userId: user._id,
        cognitoSub: validationResult.user.cognitoSub,
        expiresAt: validationResult.expiresAt
      });

      return {
        success: true,
        user,
        sessionId,
        expiresAt: validationResult.expiresAt
      };
      
    } catch (error: any) {
      console.error('Convex validation failed:', error);
      
      // For development, throw the error instead of falling back
      throw error;
    }
  };


  // Parse Cognito user from ID token (simplified for single-tenant)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const parseUserFromToken = async (): Promise<CognitoUser | null> => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken;
      
      if (!idToken) {
        return null;
      }

      // Parse the ID token to get user attributes
      const payload = JSON.parse(atob(idToken.toString().split('.')[1]));

      return {
        userId: payload.sub,
        email: payload.email,
        name: payload.name || `${payload.given_name || ''} ${payload.family_name || ''}`.trim(),
        given_name: payload.given_name,
        family_name: payload.family_name,
        role: payload['custom:role'] || 'operator',
        email_verified: payload.email_verified,
      };
    } catch (error) {
      console.error('Failed to parse user from token:', error);
      return null;
    }
  };

  // Initialize auth state when Amplify user changes
  const initializeAuth = async () => {
    // Prevent multiple simultaneous initializations
    if (initializingRef.current) {
      console.log('Auth initialization already in progress, skipping...');
      return;
    }

    if (authState.convexReady && authState.sessionId) {
      console.log('Auth already initialized, skipping...');
      return;
    }

    initializingRef.current = true;
    
    try {
      setAuthState(prev => ({ ...prev, isLoading: true, error: null }));

      // Check if we already have a valid session stored locally
      const storedSession = localStorage.getItem('alterra_session');
      if (storedSession) {
        try {
          const sessionData = JSON.parse(storedSession);
          if (sessionData.sessionId && sessionData.expiresAt > Date.now()) {
            
            // Try to validate the existing session with Convex
            const { ConvexHttpClient } = await import("convex/browser");
            const client = new ConvexHttpClient((import.meta as any).env?.VITE_CONVEX_URL || "https://api.alterra.smartcronos.io");
            
            const user = await client.query("components/auth/index:getCurrentUser" as any, {
              sessionId: sessionData.sessionId
            });

            if (user) {
              const cognitoUser: CognitoUser = {
                userId: user._id,
                email: user.email,
                name: user.name,
                role: user.role,
                email_verified: true
              };

              setAuthState({
                user: cognitoUser,
                sessionId: sessionData.sessionId,
                isLoading: false,
                error: null,
                convexReady: true,
              });
              
              initializingRef.current = false;
              return;
            }
          }
        } catch (error) {
          localStorage.removeItem('alterra_session');
        }
      }

      let convexResult;
      
      try {
        // Try to get current Amplify user
        const amplifyUser = await getCurrentUser();
        
        if (amplifyUser) {
          // Get ID token
          const session = await fetchAuthSession();
          const idToken = session.tokens?.idToken?.toString();

          if (idToken) {
            // Validate with Convex and create session using real Cognito token
            convexResult = await validateWithConvex(idToken);
          } else {
            throw new Error('No ID token available');
          }
        } else {
          throw new Error('No Amplify user');
        }
      } catch (error: any) {
        console.error('Amplify auth not available:', error.message);
        throw new Error('Authentication required - please sign in with valid credentials');
      }
      
      if (!convexResult.success) {
        throw new Error('Session creation failed');
      }

      // Create user object from Convex result
      const user: CognitoUser = {
        userId: convexResult.user._id,
        email: convexResult.user.email,
        name: convexResult.user.name,
        role: convexResult.user.role,
        email_verified: true
      };

      // Store session ID locally
      localStorage.setItem('alterra_session', JSON.stringify({
        sessionId: convexResult.sessionId,
        expiresAt: convexResult.expiresAt,
      }));

      setAuthState({
        user,
        sessionId: convexResult.sessionId,
        isLoading: false,
        error: null,
        convexReady: true,
      });

    } catch (error: any) {
      console.error('Auth initialization failed:', error);
      setAuthState({
        user: null,
        sessionId: null,
        isLoading: false,
        error: error.message || 'Authentication failed',
        convexReady: false,
      });
    } finally {
      initializingRef.current = false;
    }
  };

  // Initialize when component mounts
  useEffect(() => {
    initializeAuth();
  }, []);

  // Listen for auth state changes (when user signs in/out via Amplify UI)
  useEffect(() => {
    const handleStorageChange = () => {
      // Re-initialize when auth state changes
      initializeAuth();
    };

    // Listen for localStorage changes (Amplify Auth stores tokens there)
    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, []);

  // Handle session expiry by listening for "Session expired" errors
  useEffect(() => {
    const handleConvexError = (event: any) => {
      if (event.detail && event.detail.message && event.detail.message.includes('Session expired')) {
        localStorage.removeItem('alterra_session');
        initializeAuth();
      }
    };

    // Listen for custom session expiry events (we'd need to dispatch these from error handlers)
    window.addEventListener('convex-session-expired', handleConvexError);
    
    return () => {
      window.removeEventListener('convex-session-expired', handleConvexError);
    };
  }, []);

  return (
    <AmplifyAuthContext.Provider value={authState}>
      {children}
    </AmplifyAuthContext.Provider>
  );
}

export function useAmplifyAuth(): AmplifyAuthContextType {
  const context = useContext(AmplifyAuthContext);
  if (context === undefined) {
    throw new Error('useAmplifyAuth must be used within an AmplifyAuthProvider');
  }
  return context;
}